/**
 * Modal for managing table columns (show/hide, reorder)
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
} from '@mui/material';
import { DragIndicator as DragIndicatorIcon } from '@mui/icons-material';
import { ColumnConfig } from '../../hooks/useTablePreferences';

interface ManageColumnsModalProps {
  open: boolean;
  columns: ColumnConfig[];
  visibleColumns: string[];
  columnOrder: string[];
  onColumnsChange: (visibleColumns: string[]) => void;
  onOrderChange: (columnOrder: string[]) => void;
  onResetDefaults: () => void;
  onClose: () => void;
}

const ManageColumnsModal: React.FC<ManageColumnsModalProps> = ({
  open,
  columns,
  visibleColumns,
  columnOrder,
  onColumnsChange,
  onOrderChange,
  onResetDefaults,
  onClose,
}) => {
  const [localOrder, setLocalOrder] = useState<string[]>(columnOrder);
  const [draggedItem, setDraggedItem] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setLocalOrder(columnOrder);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleVisibilityChange = useCallback(
    (columnKey: string, isVisible: boolean) => {
      if (isVisible) {
        onColumnsChange([...visibleColumns, columnKey]);
      } else {
        onColumnsChange(visibleColumns.filter((key) => key !== columnKey));
      }
    },
    [visibleColumns, onColumnsChange]
  );

  const handleDragStart = (columnKey: string) => {
    setDraggedItem(columnKey);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (targetKey: string) => {
    if (!draggedItem || draggedItem === targetKey) return;

    const newOrder = [...localOrder];
    const draggedIndex = newOrder.indexOf(draggedItem);
    const targetIndex = newOrder.indexOf(targetKey);

    if (draggedIndex !== -1 && targetIndex !== -1) {
      newOrder.splice(draggedIndex, 1);
      newOrder.splice(targetIndex, 0, draggedItem);
      setLocalOrder(newOrder);
    }

    setDraggedItem(null);
  };

  const handleApply = () => {
    onOrderChange(localOrder);
    onClose();
  };

  const handleReset = () => {
    onResetDefaults();
    onClose();
  };

  const orderedColumns = localOrder
    .map((key) => columns.find((c) => c.key === key))
    .filter((col) => col !== undefined) as ColumnConfig[];

  return (
    <Dialog open={open} onClose={(_, reason) => { if (reason !== 'backdropClick') onClose(); }} maxWidth="sm" fullWidth sx={{ zIndex: (theme) => theme.zIndex.modal + 1000 }}>
      <DialogTitle>Manage Columns</DialogTitle>
      <DialogContent dividers>
        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
          Show/Hide Columns
        </Typography>

        <List sx={{ mb: 3 }}>
          {columns.map((column) => (
            <ListItem key={column.key} disablePadding sx={{ mb: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={visibleColumns.includes(column.key)}
                    onChange={(e) => handleVisibilityChange(column.key, e.target.checked)}
                  />
                }
                label={column.label}
                sx={{ flex: 1 }}
              />
            </ListItem>
          ))}
        </List>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
          Column Order (Drag to Reorder)
        </Typography>

        <List sx={{ bgcolor: 'grey.50', borderRadius: 1, p: 1 }}>
          {orderedColumns.map((column) => (
            <ListItem
              key={column.key}
              draggable
              onDragStart={() => handleDragStart(column.key)}
              onDragOver={handleDragOver}
              onDrop={() => handleDrop(column.key)}
              sx={{
                mb: 1,
                bgcolor: draggedItem === column.key ? 'rgba(58, 115, 128, 0.1)' : 'white',
                border: '1px solid #E5E7EB',
                borderRadius: 1,
                cursor: 'move',
                opacity: draggedItem === column.key ? 0.5 : 1,
                transition: 'all 0.2s',
                '&:hover': {
                  bgcolor: draggedItem === column.key ? 'rgba(58, 115, 128, 0.1)' : 'grey.50',
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <DragIndicatorIcon fontSize="small" color="action" />
              </ListItemIcon>
              <ListItemText primary={column.label} />
            </ListItem>
          ))}
        </List>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleReset} color="error" variant="outlined" sx={{ mr: 'auto' }}>
          Reset to Defaults
        </Button>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleApply} variant="contained">
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManageColumnsModal;
